import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  SquarePen,
  Settings,
  X,
  MoreVertical,
  Trash2,
  User2,
  Loader,
  Pencil,
  Check,
  X as CancelIcon,
  User,
  LogOut,
  History,
  ChevronDown,
  ChevronUp,
  Search,
  FolderPlus,
  FolderOpen,
  Network,
  Mic,
  Wand2,
  MessageSquare,
  Sun,
  Moon,
  PanelLeftClose,
  PanelLeft,
} from "lucide-react";
import Layout from "./ContentLayout";
import { useSession } from "../../context/SessionContext";
import { updateSessionTitleByUser, getLatestReleaseVersion, ReleaseVersion } from "../../service/apiService";
import SettingsDialog from "../Settings/SettingsDialog";
import { toast } from "sonner";
import { Dialog, Listbox, Transition } from "@headlessui/react";
import { useAuth as cognitoUseAuth } from "react-oidc-context";
import { useModel } from "../../context/ModelContext";
import { useHat } from "../../context/HatContext";
import { useAuth } from "../../context/AuthContext";
import { useUser } from "../../context/UserContext";
import { useTheme } from "../../context/ThemeContext";
import { useView } from "../../context/ViewContext";
import HatIcon from "../../icons/hat";
import { DatabaseZap } from "lucide-react";
import HatManagement from "../Settings/SettingsComponents/HatManagement";
import WorkflowManagement from "../Settings/SettingsComponents/WorkflowManagement";
import KnowledgeBase from "../Settings/SettingsComponents/KnowledgeBase";
import MobileMenu from '../MobileMenu/MobileMenu';
import Projects from "./Projects";
import AddToProjectDialog from "./AddToProjectDialog"
import ClientLogo from "../Message/ClientLogo";
import FileManagement from './FileManagement';
import HistoryManagement from './HistoryManagement';
import MeetingManagement from '../Settings/SettingsComponents/MeetingComponents/MeetingManagement';
import MeetingRecordingDot from './MeetingRecordingDot';
import { getMeetingRecordingState } from '../../context/MeetingContext';
import Tooltip from '../Tooltip/Tooltip';

const Sidebar = (props: {
  children: React.ReactNode;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  isExpanded: boolean;
  isCollapsed: boolean;
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { isCollapsed, setIsCollapsed } = props;
  const { userDetails } = useUser();
  const { resolvedTheme, setTheme } = useTheme();
  const { view, setView } = useView();
  const isDark = resolvedTheme === "dark";

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isHatsOpen, setIsHatsOpen] = useState(false);
  const [isWorkflowsOpen, setIsWorkflowsOpen] = useState(false);
  const [isBrainBanksOpen, setIsBrainBanksOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isMeetingsOpen, setIsMeetingsOpen] = useState(false);

  // Closing this modal (X, Esc, or a backdrop click — headlessui routes
  // all three here) leaves an in-progress recording running, so say so.
  // Read the state at click time rather than subscribing: the recording
  // context ticks once a second and would re-render the whole sidebar.
  const closeMeetings = () => {
    setIsMeetingsOpen(false);
    const recordingState = getMeetingRecordingState();
    if (recordingState === 'recording' || recordingState === 'paused') {
      toast.warning(
        recordingState === 'paused'
          ? 'Your meeting recording is still open and paused. Reopen Meetings to resume or save it.'
          : 'Still recording. Your meeting keeps running in the background — reopen Meetings to stop and save it.',
        { duration: 8000 }
      );
    }
  };
  const [historyKey, setHistoryKey] = useState(0);

  const iconDefaultSize = "20";
  const sidebarRef = useRef<HTMLDivElement>(null);
  const auth = cognitoUseAuth();
  const { authMode, isAuthenticated, isLoading: authLoading } = useAuth();

  const [isAddToProjectOpen, setIsAddToProjectOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [clientLogoUrl, setClientLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [logoError, setLogoError] = useState(false);
  const [clientName, setClientName] = useState<string>('');
  const [releaseVersion, setReleaseVersion] = useState<ReleaseVersion | null>(null);
  const [versionLoading, setVersionLoading] = useState(true);
  const [isFileManagementOpen, setIsFileManagementOpen] = useState(false);
  const [fileManagementKey, setFileManagementKey] = useState(0);

  const fetchClientLogo = React.useCallback(async () => {
    try {
      setLogoLoading(true);
      setLogoError(false);

      // Import the API functions
      const { getLatestClientLogoGlobal, generateClientLogoDownloadUrlGlobal } = await import("../../service/apiService");

      const latestLogo = await getLatestClientLogoGlobal();

      if (latestLogo) {
        const downloadResponse = await generateClientLogoDownloadUrlGlobal(latestLogo.id);

        if (downloadResponse?.presigned_url) {
          // Set URL directly without any validation or testing
          setClientLogoUrl(downloadResponse.presigned_url);
          setClientName(latestLogo.client_name?.trim() || '');
        } else {
          setLogoError(true);
        }
      } else {
        setClientLogoUrl(null);
        setClientName('');
      }
    } catch (error) {
      console.error('❌ Error:', error);
      setLogoError(true);
    } finally {
      setLogoLoading(false);
    }
  }, []);

  const fetchReleaseVersion = React.useCallback(async () => {
    try {
      setVersionLoading(true);
      const version = await getLatestReleaseVersion();
      setReleaseVersion(version);
    } catch (error) {
      console.error('Error fetching release version:', error);
    } finally {
      setVersionLoading(false);
    }
  }, []);

  // Fetch client logo and release version once when component mounts
  useEffect(() => {
    // Wait for auth to complete, then fetch logo and version
    if (!authLoading && isAuthenticated) {
      fetchClientLogo();
      fetchReleaseVersion();
    }
  }, [authLoading, isAuthenticated, fetchClientLogo, fetchReleaseVersion]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && !props.isExpanded) {
        props.setIsExpanded(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [props.isExpanded, props.setIsExpanded]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        props.isExpanded &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        props.setIsExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [props.isExpanded]);

  const requiredEnvVar = (name: string): string => {
    const value = process.env[name];
    if (!value) {
      throw new Error(`Missing required environment variable: ${name}`);
    }
    return value;
  };

  const signOutRedirect = () => {
    const clientId = requiredEnvVar("REACT_APP_COGNITO_CLIENT_ID");
    const logoutUri = requiredEnvVar("REACT_APP_REDIRECT_URI");
    const cognitoDomain = requiredEnvVar("REACT_APP_COGNITO_DOMAIN");
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
      logoutUri
    )}`;
  };

  const handleLogout = async () => {
    try {
      await auth.removeUser();
      toast.success("Logged out successfully");
      // Redirect to Cognito's login page
      signOutRedirect();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const openAddToProjectDialog = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setIsAddToProjectOpen(true);
  };

  const handleHistoryClick = () => {
    setIsHistoryOpen(true);
    setHistoryKey(prev => prev + 1); // Force fresh component
  };

  return (
    <div className="flex h-screen">
      {/* Add Mobile Menu */}
      <MobileMenu
        isExpanded={props.isExpanded}
        setIsExpanded={props.setIsExpanded}
      />
      <div
        ref={sidebarRef}
        className={`sidebar-container fixed inset-y-0 left-0 z-50 flex flex-col bg-surface ${
          isCollapsed ? 'w-16' : 'w-52 md:w-56 lg:w-60'
        } border-r border-border-default ${props.isExpanded ? 'mobile-active' : ''} ${
          isCollapsed ? 'collapsed' : ''
        }`}
      >
        <div className="flex flex-col justify-between h-full">
          <div className="flex-grow flex flex-col min-h-0 pt-4 overflow-y-auto">
            {/* Header row — logo (left, original 70px) and collapse toggle (right)
              * share a single row. When collapsed the rail is too narrow for the logo,
              * so only the toggle shows, centered. Toggle is hidden on mobile (the
              * drawer X handles it). `ml-auto` pins the toggle right even when no
              * logo is present. */}
            <div className={`flex items-center ${isCollapsed ? 'justify-center px-2' : 'px-5'} mb-1`}>
              {!isCollapsed && clientLogoUrl && !logoLoading && !logoError && (
                <div className="flex items-center min-w-0">
                  <ClientLogo
                    width="70"
                    height="70"
                    logoUrl={clientLogoUrl}
                    loading={logoLoading}
                    error={logoError}
                  />
                </div>
              )}
              <Tooltip label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} side={isCollapsed ? 'right' : 'bottom'}>
                <button
                  type="button"
                  onClick={() => setIsCollapsed(prev => !prev)}
                  className={`hidden md:inline-flex ${isCollapsed ? '' : 'ml-auto'} p-1.5 rounded-md text-subtle hover:bg-surface-2 hover:text-primary transition-colors`}
                  aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  {isCollapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
                </button>
              </Tooltip>
            </div>

            <div className={isCollapsed ? 'px-2 py-2' : 'px-3 py-2'}>
              {/* BrainBanks Menu Item */}
              <Tooltip label="BrainBanks" disabled={!isCollapsed}>
                <div
                  className={`flex items-center ${isCollapsed ? 'justify-center px-0' : 'px-2'} py-2 text-muted hover:bg-surface-2 rounded-md cursor-pointer`}
                  onClick={() => setIsBrainBanksOpen(true)}
                >
                  <DatabaseZap size={20} className="text-muted flex-shrink-0" strokeWidth={1.75} />
                  {!isCollapsed && <span className="ml-3 text-sm font-semibold">BrainBanks</span>}
                </div>
              </Tooltip>

              {/* Hats Menu Item */}
              <Tooltip label="Virtual Assistants" disabled={!isCollapsed}>
                <div
                  className={`flex items-center ${isCollapsed ? 'justify-center px-0' : 'px-2'} py-2 text-muted hover:bg-surface-2 rounded-md cursor-pointer ${isHatsOpen ? 'bg-surface-2' : ''
                    }`}
                  onClick={() => setIsHatsOpen(true)}
                >
                  <img
                    src={isHatsOpen ? '/va-icon.svg' : '/va-icon-bw.svg'}
                    alt="Virtual Assistant Icon"
                    className="w-5 h-5 flex-shrink-0"
                  />
                  {!isCollapsed && <span className="ml-3 text-sm font-semibold">Virtual Assistants</span>}
                </div>
              </Tooltip>

              {/* Workflows Menu Item */}
              <Tooltip label="Workflows" disabled={!isCollapsed}>
                <div
                  className={`flex items-center ${isCollapsed ? 'justify-center px-0' : 'px-2'} py-2 text-muted hover:bg-surface-2 rounded-md cursor-pointer ${isWorkflowsOpen ? 'bg-surface-2' : ''
                    }`}
                  onClick={() => setIsWorkflowsOpen(true)}
                >
                  <Network size={20} className="text-muted flex-shrink-0" strokeWidth={1.75} />
                  {!isCollapsed && <span className="ml-3 text-sm font-semibold">Workflows</span>}
                </div>
              </Tooltip>

              {/* Meetings Menu Item */}
              <Tooltip label="Meetings" disabled={!isCollapsed}>
                <div
                  className={`relative flex items-center ${isCollapsed ? 'justify-center px-0' : 'px-2'} py-2 text-muted hover:bg-surface-2 rounded-md cursor-pointer ${isMeetingsOpen ? 'bg-surface-2' : ''
                    }`}
                  onClick={() => setIsMeetingsOpen(true)}
                >
                  <Mic size={20} className="text-muted flex-shrink-0" strokeWidth={1.75} />
                  {!isCollapsed && <span className="ml-3 text-sm font-semibold">Meetings</span>}
                  {/* A recording keeps running when the modal is closed —
                      say so where the user can see it. */}
                  <span className={isCollapsed ? 'absolute translate-x-3 -translate-y-3' : 'ml-auto'}>
                    <MeetingRecordingDot />
                  </span>
                </div>
              </Tooltip>

              {/* Image Studio — swaps the primary view; toggles back to chat when active */}
              <Tooltip label={view === 'studio' ? 'Back to Chat' : 'Image Studio (BETA)'} disabled={!isCollapsed}>
                <div
                  className={`flex items-center ${isCollapsed ? 'justify-center px-0' : 'px-2'} py-2 text-muted hover:bg-surface-2 rounded-md cursor-pointer ${view === 'studio' ? 'bg-surface-brand text-primary' : ''
                    }`}
                  onClick={() => setView(view === 'studio' ? 'chat' : 'studio')}
                >
                  {view === 'studio' ? (
                    <MessageSquare size={20} className="text-violet-500 flex-shrink-0" strokeWidth={1.75} />
                  ) : (
                    <Wand2 size={20} className="text-muted flex-shrink-0" strokeWidth={1.75} />
                  )}
                  {!isCollapsed && (
                    <span className="ml-3 text-sm font-semibold">
                      {view === 'studio' ? 'Back to Chat' : 'Image Studio (BETA)'}
                    </span>
                  )}
                </div>
              </Tooltip>

              {/* Projects — full UI when expanded, single icon when collapsed (clicking expands) */}
              {isCollapsed ? (
                <Tooltip label="Projects">
                  <div
                    className="flex items-center justify-center px-0 py-2 text-muted hover:bg-surface-2 rounded-md cursor-pointer"
                    onClick={() => setIsCollapsed(false)}
                  >
                    <FolderPlus size={20} className="text-muted flex-shrink-0" />
                  </div>
                </Tooltip>
              ) : (
                <div className="mt-1">
                  <Projects />
                </div>
              )}

              {/* History Menu Item - Updated to open modal */}
              <Tooltip label="History" disabled={!isCollapsed}>
                <div
                  className={`flex items-center ${isCollapsed ? 'justify-center px-0' : 'px-2'} py-2 text-muted hover:bg-surface-2 rounded-md cursor-pointer`}
                  onClick={handleHistoryClick}
                >
                  <History size={20} className="text-muted flex-shrink-0" />
                  {!isCollapsed && <span className="ml-3 text-sm font-semibold">History</span>}
                </div>
              </Tooltip>
            </div>

            {/* File Management Button */}
            <div className={`${isCollapsed ? 'px-2' : 'px-3'} mt-2 mb-2`}>
              {!userDetails ? (
                // Loading state with shimmer effect
                <div className={`flex items-center ${isCollapsed ? 'justify-center px-0' : 'px-2'} py-2 rounded-md cursor-not-allowed opacity-60`}>
                  <div className={`animate-pulse flex items-center ${isCollapsed ? '' : 'w-full'}`}>
                    <div className="w-5 h-5 bg-gray-300 rounded flex-shrink-0"></div>
                    {!isCollapsed && <div className="ml-3 h-4 bg-gray-300 rounded flex-1"></div>}
                  </div>
                </div>
              ) : (
                // Loaded state with actual button
                <Tooltip
                  label={userDetails.role === 'admin' ? 'File Management' : 'My Files'}
                  disabled={!isCollapsed}
                >
                  <div
                    className={`flex items-center ${isCollapsed ? 'justify-center px-0' : 'px-2'} py-2 text-muted hover:bg-surface-2 rounded-md cursor-pointer`}
                    onClick={() => {
                      setIsFileManagementOpen(true);
                      setFileManagementKey(prev => prev + 1);
                    }}
                  >
                    <FolderOpen size={20} className="text-muted flex-shrink-0" />
                    {!isCollapsed && (
                      <span className="ml-3 text-sm font-semibold truncate">
                        {userDetails.role === 'admin' ? 'File Management' : 'My Files'}
                      </span>
                    )}
                  </div>
                </Tooltip>
              )}
            </div>
          </div>

          <div className={`border-t border-border-default pt-4 pb-6 ${isCollapsed ? 'px-2' : 'px-3'} flex-shrink-0`}>
            <Tooltip label="Settings" disabled={!isCollapsed}>
              <div
                className={`flex items-center ${isCollapsed ? 'justify-center px-0' : 'px-2'} py-2 text-muted hover:bg-surface-2 rounded-md cursor-pointer`}
                onClick={() => setIsSettingsOpen(true)}
              >
                <Settings size={20} className="text-muted flex-shrink-0" />
                {!isCollapsed && <span className="ml-3 text-sm font-semibold truncate">Settings</span>}
              </div>
            </Tooltip>

            {authMode === "cognito" && (
              <Tooltip label="Logout" disabled={!isCollapsed}>
                <div
                  className={`flex items-center ${isCollapsed ? 'justify-center px-0' : 'px-2'} py-2 text-muted hover:bg-surface-2 rounded-md cursor-pointer`}
                  onClick={handleLogout}
                >
                  <LogOut size={20} className="text-subtle flex-shrink-0" />
                  {!isCollapsed && <span className="ml-3 text-sm font-medium truncate">Logout</span>}
                </div>
              </Tooltip>
            )}

            {/* Version on the left, theme switch on the right — both passive utilities */}
            {isCollapsed ? (
              <div className="flex items-center justify-center py-2">
                <Tooltip label={isDark ? "Switch to light theme" : "Switch to dark theme"}>
                  <button
                    type="button"
                    onClick={() => setTheme(isDark ? "light" : "dark")}
                    className="h-8 w-8 rounded-full flex items-center justify-center bg-surface-2 hover:bg-surface-3 transition-colors"
                    aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
                  >
                    {isDark ? <Moon size={16} className="text-zinc-300" /> : <Sun size={16} className="text-amber-500" />}
                  </button>
                </Tooltip>
              </div>
            ) : (
              <div className="flex items-center px-2 py-2 text-subtle">
                {versionLoading ? (
                  <div className="ml-3 flex items-center">
                    <div className="h-3 bg-surface-3 rounded w-16 animate-pulse"></div>
                  </div>
                ) : releaseVersion ? (
                  <span className="ml-3 text-xs font-medium truncate">Version {releaseVersion.version}</span>
                ) : (
                  <span className="ml-3" />
                )}
                <div
                  role="radiogroup"
                  aria-label="Theme"
                  className="ml-auto relative inline-flex items-center bg-surface-2 rounded-full p-0.5"
                >
                  {/* Floating pill — slides between sun and moon */}
                  <span
                    aria-hidden="true"
                    className={`absolute top-0.5 left-0.5 h-7 w-7 rounded-full bg-surface-overlay shadow-sm transition-transform duration-300 ease-out ${
                      isDark ? "translate-x-7" : "translate-x-0"
                    }`}
                  />
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!isDark}
                    aria-label="Light theme"
                    onClick={() => setTheme("light")}
                    className={`relative z-10 h-7 w-7 rounded-full flex items-center justify-center transition-colors ${
                      !isDark ? "text-amber-500" : "text-subtle hover:text-primary"
                    }`}
                  >
                    <Sun size={16} />
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isDark}
                    aria-label="Dark theme"
                    onClick={() => setTheme("dark")}
                    className={`relative z-10 h-7 w-7 rounded-full flex items-center justify-center transition-colors ${
                      isDark ? "text-zinc-300" : "text-subtle hover:text-primary"
                    }`}
                  >
                    <Moon size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`content-container flex-grow w-full ${
        isCollapsed ? 'md:ml-16 lg:ml-16' : 'lg:ml-60 md:ml-56 sm:ml-52'
      } xs:ml-0 transition-all duration-300 ${isCollapsed ? 'collapsed' : ''}`}>
        <Layout>{props.children}</Layout>
      </div>

      {/* Add to Project Dialog */}
      <AddToProjectDialog
        isOpen={isAddToProjectOpen}
        onClose={() => setIsAddToProjectOpen(false)}
        sessionId={selectedSessionId}
      />

      {/* History Management Modal */}
      {isHistoryOpen && (
        <HistoryManagement
          key={historyKey}
          onClose={() => setIsHistoryOpen(false)}
          onAddToProject={openAddToProjectDialog}
        />
      )}

      {/* Virtual Assistants Modal */}
      <Transition appear show={isHatsOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setIsHatsOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 scrim" aria-hidden="true" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative bg-surface-overlay text-primary rounded-2xl px-12 py-6 text-left align-middle shadow-xl ring-1 ring-black/5 dark:ring-gray-700 transition-all w-full lg:w-[1100px] sm:w-[90vw] h-[80vh] sm:h-[70vh] lg:h-[650px] overflow-hidden">
                  <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-4">
                    <h2 className="text-xl font-semibold">Virtual Assistants</h2>
                    <button
                      onClick={() => setIsHatsOpen(false)}
                      className="rounded-full bg-transparent hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 p-2"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  <div className="overflow-y-auto h-[calc(100%-4rem)]">
                    <HatManagement onClose={() => setIsHatsOpen(false)} />
                 </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Workflows Modal */}
      <Transition appear show={isWorkflowsOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setIsWorkflowsOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 scrim" aria-hidden="true" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative bg-surface-overlay text-primary rounded-2xl px-12 py-6 text-left align-middle shadow-xl ring-1 ring-black/5 dark:ring-gray-700 transition-all w-full lg:w-[1100px] sm:w-[90vw] h-[80vh] sm:h-[70vh] lg:h-[650px] overflow-hidden">
                  <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-4">
                    <h2 className="text-xl font-semibold">Workflows</h2>
                    <button
                      onClick={() => setIsWorkflowsOpen(false)}
                      className="rounded-full bg-transparent hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 p-2"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  <div className="overflow-y-auto h-[calc(100%-4rem)]">
                    <WorkflowManagement />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* BrainBanks Modal */}
      <Transition appear show={isBrainBanksOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setIsBrainBanksOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 scrim" aria-hidden="true" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative bg-surface-overlay text-primary rounded-2xl px-12 py-6 text-left align-middle shadow-xl ring-1 ring-black/5 dark:ring-gray-700 transition-all w-full lg:w-[1100px] sm:w-[90vw] h-[80vh] sm:h-[70vh] lg:h-[650px] overflow-hidden">
                  <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-4">
                    <h2 className="text-xl font-semibold">BrainBanks</h2>
                    <button
                      onClick={() => setIsBrainBanksOpen(false)}
                      className="rounded-full bg-transparent hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 p-2"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  <div className="overflow-y-auto h-[calc(100%-4rem)]">
                    <KnowledgeBase />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Meetings Modal */}
      <Transition appear show={isMeetingsOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={closeMeetings}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 scrim" aria-hidden="true" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-2 sm:p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative bg-surface-overlay text-primary rounded-2xl px-4 sm:px-8 lg:px-12 py-4 sm:py-6 text-left align-middle shadow-xl ring-1 ring-black/5 dark:ring-gray-700 transition-all w-full lg:w-[1100px] sm:w-[90vw] h-[90vh] sm:h-[70vh] lg:h-[650px] overflow-hidden">
                  <div className="flex justify-between items-center border-b border-gray-200 pb-3 sm:pb-4 mb-3 sm:mb-4">
                    <h2 className="text-lg sm:text-xl font-semibold">Meetings</h2>
                    <button
                      onClick={closeMeetings}
                      className="rounded-full bg-transparent hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 p-2"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  <div className="overflow-y-auto h-[calc(100%-4rem)]">
                    <MeetingManagement onClose={closeMeetings} />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Settings Dialog */}
      <SettingsDialog
        isOpen={isSettingsOpen}
        setIsOpen={setIsSettingsOpen}
      />

      {/* File Management - Lazy Loaded */}
      {isFileManagementOpen && (
        <FileManagement
          key={fileManagementKey}
          onClose={() => setIsFileManagementOpen(false)}
        />
      )}
    </div>
  );
};

export default Sidebar;